import {
    HTTP_INTERCEPTORS,
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, delay, retryWhen, switchMap, take } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { ModalService } from '../../shared/services/modal.service';
import { ToastrService } from 'ngx-toastr';
import { decrireErreurHttp } from '../../shared/utils/erreur-http.util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    protected keyCloakService = inject(KeycloakService);
    protected toastrService = inject(ToastrService);
    protected modalService = inject(ModalService);

    private maxRetries = 3;
    private retryDelay = 2000;

    /**
     * Intercepte les requêtes HTTP pour ajouter le token Keycloak
     * Note: addTokenToHeader() gère automatiquement le refresh du token si nécessaire
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip si pas connecté ou URL exclue
        if (!this.keyCloakService.isLoggedIn() || this.shouldSkipAuth(req.url)) {
            return next.handle(req);
        }

        // addTokenToHeader() fait automatiquement un updateToken() si le token expire bientôt
        // Donc pas besoin de vérifier manuellement l'expiration ici
        return from(this.keyCloakService.addTokenToHeader(req.headers)).pipe(
            catchError(err => {
                // Erreur lors de l'ajout du token seulement (pas les erreurs HTTP)
                console.warn('[AuthInterceptor] Erreur lors de l\'ajout du token, tentative sans auth:', err);
                return from(Promise.resolve(req.headers));
            }),
            switchMap(headers => {
                const cloneReq = req.clone({ headers });
                return this.handleRequest(cloneReq, next);
            })
        );
    }

    private handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            retryWhen(errors =>
                errors.pipe(
                    switchMap((error, index) => {
                        // Retry SEULEMENT pour les 504 (timeout), pas les 500 (erreur serveur)
                        if (index < this.maxRetries - 1 && error.status === 504 && !this.isTelephonyStatisticsRequest(req)) {
                            console.log(`Retry ${index + 1}/${this.maxRetries - 1} pour timeout 504...`);
                            return of(error).pipe(delay(this.retryDelay));
                        }
                        return throwError(() => error);
                    }),
                    take(this.maxRetries)
                )
            ),
            catchError((error: HttpErrorResponse) => {
                this.toastrService.clear();

                if (error.status === 401) {
                    // Token expiré pendant la requête, tenter un refresh
                    return from(this.keyCloakService.updateToken()).pipe(
                        switchMap(() => {
                            const retryReq = req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${this.keyCloakService.getKeycloakInstance().token}`
                                }
                            });
                            return next.handle(retryReq);
                        }),
                        catchError(err => {
                            this.keyCloakService.logout();
                            return throwError(() => err);
                        })
                    );
                } else if (error.status === 404) {
                    console.error('Not Found', error);
                } else if (error.status === 500) {
                    this.modalService.open('timeoutModal', decrireErreurHttp(error, req.method, req.body));
                    console.error('Internal Server Error', error);
                } else if (error.status === 504) {
                    this.modalService.open('timeoutModal', decrireErreurHttp(error, req.method, req.body));
                    console.error('Gateway Timeout après plusieurs tentatives', error);
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Statistiques téléphonie : un 504 ne stoppe pas le calcul côté serveur, qui interroge
     * les IPBX sur toute la période. Le relancer empilerait des calculs identiques.
     */
    private isTelephonyStatisticsRequest(req: HttpRequest<unknown>): boolean {
        return req.url.includes('/telephonie/clients/statistiques/')
            || (req.url.includes('/telephonie/') && req.urlWithParams.includes('statistics=true'));
    }

    /**
     * Détermine si une URL doit être exclue de l'ajout du token
     */
    private shouldSkipAuth(url: string): boolean {
        const excludedPaths = [
            '/assets',      // Fichiers statiques
            '/github',      // API GitHub
            '/weather',     // API Météo

            'netbox/api',   // API Netbox
        ];

        // Exclure les URLs RC-STOCKAGE
        const rcStoragePatterns = [
            'http://127.0.0.1:8001',
        ];

        if (rcStoragePatterns.some(pattern => url.includes(pattern))) {
            return true;
        }

        return excludedPaths.some(path => url.includes(path));
    }
}

export const authInterceptorProviders = [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];